import { Component, NgZone, OnInit, OnDestroy } from '@angular/core';

import { Router, NavigationEnd } from '@angular/router';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { Deeplinks } from '@ionic-native/deeplinks/ngx';
import { SplashScreen } from '@ionic-native/splash-screen/ngx';
import { StatusBar } from '@ionic-native/status-bar/ngx';
import { AlertController, ModalController, NavController, Platform } from '@ionic/angular';
import { LoginPage } from './pages/login/login.page';
import { Subscription } from 'rxjs';
import packageInfo from '../../package.json';
import { AlertService } from './services/alert.service';
import { HttpService } from './services/http.service';
import { UserService } from './services/user.service';
import { NotificationService } from './services/notification.service';
import { AuthService } from './services/auth.service';
import { App } from '@capacitor/app';
import { StatusBar as CapacitorStatusBar, Style } from '@capacitor/status-bar';
import { OnboardingSequenceGuard } from './guards/onboarding-sequence.guard';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private backdropFocusHandler = (e: FocusEvent) => {
    const target = e.target as HTMLElement;
    if (target?.tagName === 'ION-BACKDROP' && target.getAttribute('aria-hidden') === 'true') {
      target.blur();
    }
  };

  public selectedIndex = 0;
  public appPagesUser = [
    {
      title: 'Home',
      param: 'home',
      icon: 'home-outline',
      needsLogin: false,
    },
    {
      title: 'Meu Perfil',
      param: 'profile',
      icon: 'person-circle-outline',
    },
    {
      title: 'Meus Pedidos',
      param: 'orders',
      icon: 'card-outline',
      needsLogin: true,
    },
    {
      title: 'Notificações',
      param: 'profile/notifications',
      icon: 'notifications-outline',
      needsLogin: true,
    },
  ];
  public notificationUnreadCount = 0;
  public appPagesExpert = [
    {
      title: 'Dicas do especialista',
      param: 'tip',
      icon: 'bulb-outline',
    },
    {
      title: 'Fale com o especialista',
      param: 'toWhatsapp',
      icon: 'chatbubble-outline',
    },
  ];
  public phone: string = '5531984659860';
  public user: any = {};
  public seller: any = null;
  public loginStatus: boolean;
  public appVersion: string = packageInfo.version;
  
  // WhatsApp help button
  public whatsappHelpVisible: boolean = true;

  // Checkout button properties
  public showCheckoutButton: boolean = false;
  public cartItemCount: number = 0;
  public cartTotalPrice: number = 0;
  public isOnCheckoutPage: boolean = false; // Also includes payment-method page
  public isRegisterPage: boolean = false;
  public isMenuCollapsed: boolean = false; // Menu collapse state for desktop
  public isInOnboardingMode: boolean = false; // True when seller is in onboarding flow

  private userSubscription: Subscription;
  private notificationCountSubscription: Subscription;

  constructor(
    private platform: Platform,
    private splashScreen: SplashScreen,
    private statusBar: StatusBar,
    private httpService: HttpService,
    private navCtrl: NavController,
    private router: Router,
    private alertCtrl: AlertController,
    private alertSrvc: AlertService,
    private iab: InAppBrowser,
    private deepLinks: Deeplinks,
    private ngZone: NgZone,
    private userService: UserService,
    private modalCtrl: ModalController,
    private notificationService: NotificationService,
    private authService: AuthService
  ) {}

  async ngOnInit() {
    // Prevent aria-hidden violation: ion-backdrop must not retain focus (e.g. ion-select popover)
    document.addEventListener('focusin', this.backdropFocusHandler, true);

    // Configure the native status bar once the platform is ready. Without this
    // the status bar stays at the OS default (black strip with no background
    // color); we want a white background with dark icons to match the header.
    this.platform.ready().then(() => this.configureStatusBar());

    if (this.platform.is('cordova')) {
      this.listenToDeepLinks();
    }

    // Subscribe to user changes
    this.userSubscription = this.userService.user$.subscribe(async user => {
      this.user = user;
      console.log('User updated:', this.user);
      if (user?.id) {
        this.notificationService.onUserLoggedIn();
        // Fetch seller data if user is a seller
        if (user?.type === 'seller') {
          try {
            this.seller = await this.httpService.get('seller/' + user.id, this.httpService.getOpts()).toPromise();
          } catch (error) {
            console.log('Error fetching seller data:', error);
          }
        }
      } else {
        this.seller = null; // Clear seller data when user logs out
      }
    });

    this.notificationCountSubscription = this.notificationService.unreadCount$.subscribe(
      count => (this.notificationUnreadCount = count)
    );
    
    // Initialize login status (so menu visibility is correct on load)
    await this.updateLogin();
    
    // Initialize register page status
    const registerPaths = ['/email', '/phone', '/password', '/address', '/seller-select', '/logged-seller/store', '/plans'];
    this.isRegisterPage = registerPaths.some(path => window.location.pathname.includes(path));
    
    // Initialize checkout page status (hide floating cart button when already on checkout)
    this.isOnCheckoutPage = this.router.url?.includes('/checkout') || this.router.url?.includes('/payment-method') || false;
    
    // Initialize cart data
    this.updateCartData();
    
    // Listen for cart updates from other components. Run inside Angular's zone so
    // the floating "Ver Carrinho" banner re-renders immediately when an item is
    // added from a product/service card (without needing a navigation to refresh).
    window.addEventListener('cartUpdated', () => {
      this.ngZone.run(() => this.updateCartData());
    });
    
    this.router.events.subscribe(async (event) => {
      if (event instanceof NavigationEnd) {
        await this.updateLogin();
        this.updateCartData(); // Update cart on navigation
        
        // Check if user is on checkout or payment method page
        this.isOnCheckoutPage = event.url?.includes('/checkout') || event.url?.includes('/payment-method') || false;
        
        // Check if user is on register or login page
        const registerPaths = ['/email', '/phone', '/password', '/address', '/seller-select', '/logged-seller/store'];
        this.isRegisterPage = registerPaths.some(path => event.url?.includes(path));

        // Check if user is in onboarding mode
        this.isInOnboardingMode = OnboardingSequenceGuard.isInOnboarding();

        if (this.user?.type === 'seller') {
          try {
            const seller: any = await this.httpService.get('seller/' + this.user.id, this.httpService.getOpts()).toPromise();
            this.seller = seller; // Store seller data for avatar
            console.log('seller', seller);
            const isDocumentViewerPage = event.url?.includes('document-viewer');

            // Check onboarding progress and redirect accordingly
            if (!isDocumentViewerPage) {
              // First check if onboarding is complete via localStorage (most reliable after save)
              const isOnboardingComplete = !OnboardingSequenceGuard.isInOnboarding();

              if (!isOnboardingComplete) {
                // If seller hasn't selected a plan or confirmed terms
                if (!seller.planId || !this.user.confirmedTerms) {
                  if (!event.url?.includes('/plans')) {
                    await this.navCtrl.navigateRoot('/plans');
                    return;
                  }
                }
                // If seller hasn't completed store data (no document or no subaccount)
                else if (!seller.document || !seller.subaccountId) {
                  if (!event.url?.includes('/logged-seller/store')) {
                    await this.navCtrl.navigateRoot('/logged-seller/store');
                    return;
                  }
                }
              }
            }
          } catch (error) {
            console.log(error);
            if (error?.error?.code === 'seller_not_active') {
              return;
            }
            // If seller not found, redirect to plans to create seller
            if (!event.url?.includes('/plans')) {
              await this.navCtrl.navigateRoot('/plans');
            }
            return;
          }
          // Sellers already have their CNPJ from store setup, so we create their Asaas customer
          // account silently in the background (done in store.page.ts after saving CNPJ)
          // No need to show customer-document-modal for sellers
        }
      }
    });
    console.log('user', this.user);
  }

  /**
   * Sets the native status bar to a white background with dark content so it
   * blends with the light app header instead of rendering as a black strip.
   * No-op on the web where the StatusBar plugin isn't available.
   */
  private async configureStatusBar() {
    if (!this.platform.is('capacitor')) {
      return;
    }
    try {
      // Style.Light = dark icons/text on a light background.
      await CapacitorStatusBar.setStyle({ style: Style.Light });
      // Android needs the background color set explicitly; iOS ignores it.
      if (this.platform.is('android')) {
        await CapacitorStatusBar.setBackgroundColor({ color: '#ffffff' });
      }
      // Reserve space for the status bar instead of drawing under it.
      await CapacitorStatusBar.setOverlaysWebView({ overlay: false });
    } catch (error) {
      console.log('Error configuring status bar:', error);
    }
  }

  ngOnDestroy() {
    document.removeEventListener('focusin', this.backdropFocusHandler, true);
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    if (this.notificationCountSubscription) {
      this.notificationCountSubscription.unsubscribe();
    }
  }


  dismissWhatsappHelp(event: Event) {
    event.stopPropagation();
    this.whatsappHelpVisible = false;
  }

  navigateMenu(p) {
    if (p === 'toWhatsapp') {
      const message = this.isRegisterPage ? 'Olá! Estou no processo de cadastro e gostaria de tirar uma dúvida.' : '';
      const browser = this.iab.create(
        'https://api.whatsapp.com/send/?phone=' + this.phone + '&text=' + encodeURIComponent(message) + '&app_absent=0',
        '_system'
      );
      browser.show();
    } else {
      this.navCtrl.navigateForward(p);
    }
  }

  listenToDeepLinks() {
    this.deepLinks.routeWithNavController(this.navCtrl, {}).subscribe(
      async (data) => {
        await this.ngZone.run(async () => {
          await this.navCtrl.navigateForward(data.$link.path);
        });
      },
      async (data) => {
        if (!data?.$link) {
          throw data;
        }
        await this.ngZone.run(async () => {
          await this.navCtrl.navigateForward(data.$link.path);
        });
      }
    );
  }

  async toLogout() {
    const alert = await this.alertCtrl.create({
      header: 'Tem certeza que deseja sair?',
      cssClass: 'custom-alert',
      mode: 'ios',
      buttons: [
        {
          text: 'Cancelar',
          role: 'cancel',
          cssClass: 'button-cancel',
        },
        {
          text: 'Ok',
          role: 'ok',
          cssClass: 'button-confirm',
          handler: async () => {
            await this.httpService.get('logout').toPromise();
            localStorage.clear();
            localStorage.setItem('loginStatusDB', JSON.stringify(false));
            this.userService.clearUser(); // Clear user from UserService
            this.seller = null; // Clear seller data
            location.replace('home');
          },
        },
      ],
      backdropDismiss: false,
    });
    await alert.present();
  }

  async toLogin() {
    const modal = await this.modalCtrl.create({
      component: LoginPage,
      cssClass: 'login-modal-fullscreen',
      mode: 'ios',
      backdropDismiss: true,
    });
    await modal.present();
  }

  async updateLogin() {
    let loginJSON = localStorage.getItem('loginStatusDB');
    const userDB = localStorage.getItem('userDB');
    const parsedUserDB = JSON.parse(userDB);
    if (loginJSON || !parsedUserDB?.user) {
      this.loginStatus = JSON.parse(loginJSON);
      // User will be updated automatically through the UserService subscription
      if (parsedUserDB?.user?.id) {
        await this.userService.updateUser(parsedUserDB.user.id);
      }
    }
  }

  updateCartData() {
    try {
      // Get cart data from localStorage
      const cartData = localStorage.getItem('cartDB');
      if (cartData) {
        const cart = JSON.parse(cartData);
        // Count total units (sum of quantities), so the badge also updates when
        // the same product is added again and only its quantity increases.
        this.cartItemCount = cart.reduce((count: number, item: any) => {
          return count + (Number(item.amount) || 0);
        }, 0);
        this.cartTotalPrice = cart.reduce((total: number, item: any) => {
          return total + (item.price * item.amount);
        }, 0);
        this.showCheckoutButton = this.loginStatus && this.cartItemCount > 0;
      } else {
        this.cartItemCount = 0;
        this.cartTotalPrice = 0;
        this.showCheckoutButton = false;
      }
    } catch (error) {
      console.error('Error updating cart data:', error);
      this.cartItemCount = 0;
      this.cartTotalPrice = 0;
      this.showCheckoutButton = false;
    }
  }

  toggleMenuCollapse() {
    this.isMenuCollapsed = !this.isMenuCollapsed;
  }
}
